import { useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import ConfirmModal from "../components/ConfirmModal";
import { crashlytics } from "../services/firebase";

export function useConfirmUnfollow() {
  const { unfollowUser } = useAuth();
  const { addToast } = useToast();
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    userId: string | null;
    onSuccess?: () => void;
  }>({ isOpen: false, userId: null });

  const confirmUnfollow = useCallback(
    (userId: string, onSuccess?: () => void) => {
      setModalState({ isOpen: true, userId, onSuccess });
    },
    [],
  );

  const handleConfirm = useCallback(async () => {
    if (!modalState.userId) return;
    try {
      await unfollowUser(modalState.userId);
      addToast("Successfully unfollowed user.", "success");
      if (modalState.onSuccess) modalState.onSuccess();
    } catch (error) {
      crashlytics.logError(
        `Error unfollowing user. Error: ${JSON.stringify(error)}`,
        "useConfirmUnfollow",
        error,
      );
      addToast("Failed to unfollow user. Please try again.", "error");
    }
    setModalState({ isOpen: false, userId: null });
  }, [modalState, unfollowUser, addToast]);

  const handleCancel = useCallback(() => {
    setModalState({ isOpen: false, userId: null });
  }, []);

  const ConfirmUnfollowModal = (
    <ConfirmModal
      isOpen={modalState.isOpen}
      title="Unfollow User"
      message="Unfollow this user? They will need to approve your request if you want to follow them again later."
      confirmText="Unfollow"
      confirmStyle="danger"
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { confirmUnfollow, ConfirmUnfollowModal };
}
